<template>
  <div class="faq-page">
    <div class="faq-content">
      <!-- 页面头部 -->
      <van-nav-bar
        :title="$t('faq.title')"
        left-arrow
        @click-left="$router.back()"
      />

      <!-- 搜索和分类区域 -->
      <div class="header-section">
        <div class="search-box">
          <van-search
            v-model="searchKeyword"
            :placeholder="$t('faq.searchPlaceholder')"
            shape="round"
            background="transparent"
            @search="onSearch"
          />
        </div>

        <div class="category-tabs">
          <div
            v-for="category in categories"
            :key="category.value"
            :class="['category-tab', { active: activeCategory === category.value }]"
            @click="setActiveCategory(category.value)"
          >
            {{ category.text }}
          </div>
        </div>
      </div>

      <!-- 平铺式问题网格 -->
      <div class="questions-grid">
        <div
          v-for="question in filteredQuestions"
          :key="question.id"
          :class="['question-card', { expanded: activeQuestion === question.id }]"
          @click="toggleQuestion(question.id)"
        >
          <div class="question-header">
            <h3 class="question-title">
              {{ question.title }}
            </h3>
            <div class="question-meta">
              <span class="question-category">{{ getCategoryName(question.category) }}</span>
              <van-icon
                :name="activeQuestion === question.id ? 'arrow-up' : 'arrow-down'"
                class="expand-icon"
              />
            </div>
          </div>

          <div
            v-if="activeQuestion === question.id"
            class="question-content"
          >
            <div class="answer-text">
              <p>{{ question.answer }}</p>
            </div>

            <div
              v-if="question.relatedLinks && question.relatedLinks.length"
              class="related-links"
            >
              <h4>{{ $t('faq.relatedLinks') }}</h4>
              <div class="links-grid">
                <a
                  v-for="link in question.relatedLinks"
                  :key="link.text"
                  href="#"
                  class="link-button"
                  @click.prevent="handleLinkClick(link.url)"
                >
                  {{ link.text }}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <div
        v-if="filteredQuestions.length === 0"
        class="empty-state"
      >
        <div class="empty-content">
          <van-icon
            name="search"
            class="empty-icon"
          />
          <h3>{{ $t('faq.noQuestionsFound') }}</h3>
          <p>{{ $t('faq.tryDifferentKeywords') }}</p>
          <van-button
            round
            type="primary"
            class="reset-button"
            @click="resetSearch"
          >
            {{ $t('faq.resetSearch') }}
          </van-button>
        </div>
      </div>

      <!-- 底部帮助信息 -->
<!--      <div class="help-section">-->
<!--        <div class="help-card">-->
<!--          <van-icon-->
<!--            name="service"-->
<!--            class="help-icon"-->
<!--          />-->
<!--          <div class="help-content">-->
<!--            <h3>{{ $t('faq.needMoreHelp') }}</h3>-->
<!--            <p>{{ $t('faq.contactDescription') }}</p>-->
<!--            <van-button-->
<!--              round-->
<!--              type="primary"-->
<!--              class="contact-button"-->
<!--              @click="handleContact"-->
<!--            >-->
<!--              {{ $t('faq.contactUs') }}-->
<!--            </van-button>-->
<!--          </div>-->
<!--        </div>-->
<!--      </div>-->
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { showToast } from 'vant'

export default {
  name: 'FAQPage',
  setup () {
    const { t } = useI18n()
    const router = useRouter()

    // 搜索关键词
    const searchKeyword = ref('')

    // 当前激活的分类
    const activeCategory = ref('all')

    // 当前展开的问题
    const activeQuestion = ref(null)

    // 分类列表
    const categories = ref([
      { text: t('faq.categories.all'), value: 'all' },
      { text: t('faq.categories.cooperation'), value: 'cooperation' },
      { text: t('faq.categories.application'), value: 'application' },
      { text: t('faq.categories.technical'), value: 'technical' }
      // { text: t('faq.categories.account'), value: 'account' }
    ])

    // 问题数据
    const questions = ref([
      {
        id: 1,
        category: 'cooperation',
        title: t('faq.questions.q1.title'),
        answer: t('faq.questions.q1.answer'),
        relatedLinks: [
          { text: t('faq.questions.q1.links.department'), url: '/cooperation/departmentStore' },
          { text: t('faq.questions.q1.links.brand'), url: '/cooperation/brandApplication' }
        ]
      },
      {
        id: 2,
        category: 'cooperation',
        title: t('faq.questions.q2.title'),
        answer: t('faq.questions.q2.answer'),
        relatedLinks: [
          { text: t('faq.questions.q2.links.designer'), url: '/cooperation/designerApplication' },
          { text: t('faq.questions.q2.links.officer'), url: '/cooperation/officerApplication' }
        ]
      },
      {
        id: 3,
        category: 'application',
        title: t('faq.questions.q3.title'),
        answer: t('faq.questions.q3.answer'),
        relatedLinks: []
      },
      {
        id: 4,
        category: 'application',
        title: t('faq.questions.q4.title'),
        answer: t('faq.questions.q4.answer'),
        relatedLinks: []
      },
      {
        id: 5,
        category: 'technical',
        title: t('faq.questions.q5.title'),
        answer: t('faq.questions.q5.answer'),
        relatedLinks: []
      },
      // {
      //   id: 6,
      //   category: 'technical',
      //   title: t('faq.questions.q6.title'),
      //   answer: t('faq.questions.q6.answer'),
      //   relatedLinks: []
      // },
      // {
      //   id: 7,
      //   category: 'account',
      //   title: t('faq.questions.q7.title'),
      //   answer: t('faq.questions.q7.answer'),
      //   relatedLinks: []
      // },
      // {
      //   id: 8,
      //   category: 'account',
      //   title: t('faq.questions.q8.title'),
      //   answer: t('faq.questions.q8.answer'),
      //   relatedLinks: []
      // },
      {
        id: 9,
        category: 'cooperation',
        title: t('faq.questions.q9.title'),
        answer: t('faq.questions.q9.answer'),
        relatedLinks: []
      },
      {
        id: 10,
        category: 'application',
        title: t('faq.questions.q10.title'),
        answer: t('faq.questions.q10.answer'),
        relatedLinks: []
      }
    ])

    // 过滤后的问题列表
    const filteredQuestions = computed(() => {
      let filtered = questions.value

      // 按分类过滤
      if (activeCategory.value !== 'all') {
        filtered = filtered.filter(q => q.category === activeCategory.value)
      }

      // 按关键词搜索
      if (searchKeyword.value) {
        const keyword = searchKeyword.value.toLowerCase()
        filtered = filtered.filter(q =>
          q.title.toLowerCase().includes(keyword) ||
            q.answer.toLowerCase().includes(keyword)
        )
      }

      return filtered
    })

    // 获取分类名称
    const getCategoryName = (categoryValue) => {
      const category = categories.value.find(cat => cat.value === categoryValue)
      return category ? category.text : ''
    }

    // 设置激活分类
    const setActiveCategory = (category) => {
      activeCategory.value = category
      activeQuestion.value = null // 重置展开的问题
    }

    // 切换问题展开状态
    const toggleQuestion = (questionId) => {
      activeQuestion.value = activeQuestion.value === questionId ? null : questionId
    }

    // 搜索处理
    const onSearch = () => {
      // 搜索逻辑已在计算属性中处理
    }

    // 重置搜索
    const resetSearch = () => {
      searchKeyword.value = ''
      activeCategory.value = 'all'
      activeQuestion.value = null
    }

    // 处理链接点击
    const handleLinkClick = (url) => {
      console.log('url', url)
      router.push({
        path: url
      })
      // if (url.startsWith('/')) {
      //   router.push({
      //     path: url
      //   })
      // }
      // else {
      //   window.open(url, '_blank')
      // }
    }

    // 联系客服
    const handleContact = () => {
      showToast(t('faq.contactToast'))
      // 在实际应用中，这里可以跳转到客服页面或打开客服对话框
    }

    // 页面加载时展开第一个问题
    onMounted(() => {
      if (questions.value.length > 0) {
        // PC端不自动展开问题，让用户自由选择
      }
    })

    return {
      searchKeyword,
      activeCategory,
      activeQuestion,
      categories,
      filteredQuestions,
      getCategoryName,
      setActiveCategory,
      toggleQuestion,
      onSearch,
      resetSearch,
      handleLinkClick,
      handleContact
    }
  }
}
</script>

<style lang="scss" scoped>
.faq-page {
  min-height: 100vh;
  background-color: #f7f8fa;
}

.faq-content {
  padding: 16px;
  padding-bottom: constant(safe-area-inset-bottom);
  padding-bottom: env(safe-area-inset-bottom);

  @media (min-width: 1200px) {
    max-width: 1200px;
    margin: 0 auto;
    padding: 24px;
  }
}

/* 头部区域 */
.header-section {
  margin-bottom: 24px;

  .search-box {
    margin-bottom: 20px;

    :deep(.van-search) {
      padding: 0;

      .van-search__content {
        background: white;
        border-radius: 12px;
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
      }
    }
  }

  .category-tabs {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding-bottom: 8px;

    .category-tab {
      padding: 8px 16px;
      background: white;
      border-radius: 20px;
      font-size: 14px;
      font-weight: 500;
      color: #64748b;
      cursor: pointer;
      transition: all 0.3s ease;
      white-space: nowrap;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

      &:hover {
        background: #f1f5f9;
        transform: translateY(-1px);
      }

      &.active {
        background: #3b82f6;
        color: white;
        box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
      }
    }
  }
}

/* 问题网格 */
.questions-grid {
  display: grid;
  gap: 20px;
  margin-bottom: 40px;

  /* 移动端单列布局 */
  grid-template-columns: 1fr;

  @media (min-width: 768px) {
    /* 平板端两列布局 */
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1200px) {
    /* PC端三列布局 */
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
  }

  .question-card {
    background: white;
    border-radius: 16px;
    padding: 20px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    cursor: pointer;
    transition: all 0.3s ease;
    border: 2px solid transparent;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
      border-color: #e2e8f0;
    }

    &.expanded {
      border-color: #3b82f6;
      box-shadow: 0 8px 25px rgba(59, 130, 246, 0.15);
    }

    .question-header {
      .question-title {
        font-size: 16px;
        font-weight: 600;
        color: #1e293b;
        margin: 0 0 12px;
        line-height: 1.4;
      }

      .question-meta {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .question-category {
          font-size: 12px;
          color: #64748b;
          background: #f1f5f9;
          padding: 4px 8px;
          border-radius: 12px;
        }

        .expand-icon {
          color: #94a3b8;
          font-size: 16px;
          transition: transform 0.3s ease;
        }
      }
    }

    .question-content {
      margin-top: 16px;
      padding-top: 16px;
      border-top: 1px solid #f1f5f9;
      animation: slideDown 0.3s ease;

      .answer-text {
        p {
          margin: 0;
          color: #475569;
          line-height: 1.6;
          font-size: 14px;
        }
      }

      .related-links {
        margin-top: 16px;

        h4 {
          font-size: 14px;
          font-weight: 600;
          color: #374151;
          margin: 0 0 12px;
        }

        .links-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 8px;

          @media (min-width: 768px) {
            grid-template-columns: 1fr 1fr;
          }

          .link-button {
            display: block;
            padding: 8px 12px;
            background: #f8fafc;
            color: #3b82f6;
            text-decoration: none;
            border-radius: 8px;
            font-size: 12px;
            font-weight: 500;
            text-align: center;
            transition: all 0.2s ease;

            &:hover {
              background: #3b82f6;
              color: white;
            }
          }
        }
      }
    }
  }
}

/* 空状态 */
.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;

  .empty-content {
    text-align: center;

    .empty-icon {
      font-size: 64px;
      color: #cbd5e1;
      margin-bottom: 16px;
    }

    h3 {
      font-size: 18px;
      font-weight: 600;
      color: #475569;
      margin: 0 0 8px;
    }

    p {
      font-size: 14px;
      color: #64748b;
      margin: 0 0 20px;
    }

    .reset-button {
      height: 40px;
      padding: 0 24px;
    }
  }
}

/* 帮助区域 */
.help-section {
  .help-card {
    background: linear-gradient(135deg, #1e3a8a, #3b82f6);
    border-radius: 16px;
    padding: 24px;
    color: white;
    display: flex;
    align-items: center;
    gap: 16px;

    .help-icon {
      font-size: 32px;
      flex-shrink: 0;
    }

    .help-content {
      flex: 1;

      h3 {
        font-size: 18px;
        font-weight: 600;
        margin: 0 0 8px;
      }

      p {
        font-size: 14px;
        margin: 0 0 16px;
        opacity: 0.9;
        line-height: 1.5;
      }

      .contact-button {
        background: rgba(255, 255, 255, 0.2);
        border: 1px solid rgba(255, 255, 255, 0.3);
        color: white;
        height: 40px;

        &:hover {
          background: rgba(255, 255, 255, 0.3);
        }
      }
    }
  }
}

/* 动画 */
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* PC端优化 */
@media (min-width: 1200px) {
  .faq-page {
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    padding: 40px 0;
  }

  .faq-content {
    background: white;
    border-radius: 24px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0;
    overflow: hidden;
  }

  .van-nav-bar {
    background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);

    :deep(.van-nav-bar__content) {
      height: 70px;

      .van-nav-bar__title {
        font-size: 22px;
        font-weight: 600;
        color: white;
      }

      .van-icon-arrow-left {
        color: white;
        font-size: 20px;
      }
    }
  }

  .header-section,
  .questions-grid,
  .help-section {
    padding: 0 32px;
  }

  .header-section {
    padding-top: 24px;
    padding-bottom: 0;

    .search-box {
      :deep(.van-search) {
        .van-search__content {
          border-radius: 16px;

          .van-field__control {
            font-size: 16px;
            padding: 16px 0;
          }
        }
      }
    }

    .category-tabs {
      .category-tab {
        padding: 10px 20px;
        font-size: 15px;
      }
    }
  }

  .questions-grid {
    padding-top: 24px;
    padding-bottom: 24px;

    .question-card {
      padding: 24px;

      .question-header {
        .question-title {
          font-size: 17px;
        }

        .question-meta {
          .question-category {
            font-size: 13px;
          }
        }
      }

      .question-content {
        .answer-text p {
          font-size: 15px;
        }

        .related-links {
          .links-grid {
            grid-template-columns: 1fr 1fr;

            .link-button {
              font-size: 13px;
              padding: 10px 12px;
            }
          }
        }
      }
    }
  }

  .help-section {
    padding-bottom: 32px;

    .help-card {
      padding: 32px;

      .help-icon {
        font-size: 40px;
      }

      .help-content {
        h3 {
          font-size: 20px;
        }

        p {
          font-size: 15px;
        }

        .contact-button {
          height: 44px;
          padding: 0 28px;
        }
      }
    }
  }
}

/* 大屏幕PC进一步优化 */
@media (min-width: 1600px) {
  .faq-content {
    max-width: 1400px;
  }

  .questions-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

/* 移动端适配 */
@media (max-width: 767px) {
  .faq-content {
    padding: 12px;
  }

  .header-section .category-tabs {
    .category-tab {
      font-size: 13px;
      padding: 6px 12px;
    }
  }
}
</style>
